"use client";
import { useEffect, useRef, useState } from "react";
import type { MapRef } from "react-map-gl";
import { twMerge } from "tailwind-merge";
import { ControlButton } from "./primitives/ControlButton";
import { ControlGroup } from "./primitives/ControlGroup";
import type { ControlPosition } from "./primitives/utils";

interface MapNavigationControlProps {
  mapRef: React.RefObject<MapRef | null>;
  position?: ControlPosition;
  className?: string;
  /**
   * Custom positioning - overrides position prop
   * Example: { top: '20px', right: '20px' }
   */
  style?: React.CSSProperties;
  /**
   * Show compass button (mobile only - hidden on desktop)
   * @default true
   */
  showCompass?: boolean;
  /**
   * Show geolocate button
   * @default true
   */
  showGeolocate?: boolean;
  /**
   * Show reset zoom button at the top of the control group
   * Only appears when the view has changed from the initial position
   * @default true
   */
  showResetZoom?: boolean;
  /**
   * Callback when reset zoom is clicked
   */
  onResetZoom?: () => void;
  /**
   * Callback when compass is clicked
   */
  onCompassClick?: () => void;
  /**
   * Callback when geolocate is clicked
   */
  onGeolocateClick?: () => void;
}

/**
 * Unified navigation control for maps
 *
 * Provides compass (mobile only), geolocate, and reset zoom buttons with consistent styling.
 * This is the primary navigation control for InteractiveMap.
 *
 * The reset zoom button automatically appears/disappears based on whether the user
 * has moved away from the initial view position.
 *
 * @example
 * <MapNavigationControl
 *   mapRef={mapRef}
 *   position="bottom-right"
 *   showCompass={true}
 *   showGeolocate={true}
 *   showResetZoom={true}
 * />
 */
export function NavigationControl({
  mapRef,
  position = "bottom-right",
  className,
  style,
  showCompass = true,
  showGeolocate = true,
  showResetZoom = true,
  onResetZoom,
  onCompassClick,
  onGeolocateClick,
}: MapNavigationControlProps) {
  const [isGeolocating, setIsGeolocating] = useState(false);
  const isMountedRef = useRef(true);

  // Cleanup on unmount to prevent race condition
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleCompassClick = () => {
    if (onCompassClick) {
      onCompassClick();
    } else if (mapRef.current) {
      // Reset bearing to 0 (north up)
      mapRef.current.easeTo({
        bearing: 0,
        duration: 500,
      });
    }
  };

  const handleGeolocateClick = async () => {
    // Prevent multiple simultaneous requests
    if (isGeolocating) {
      return;
    }

    if (onGeolocateClick) {
      onGeolocateClick();
    } else {
      // Use browser geolocation API
      if (!navigator.geolocation) {
        console.warn("Geolocation is not supported by this browser.");
        return;
      }

      // Check permissions first if the API is available
      // Note: Safari/iOS doesn't support permissions.query for geolocation
      if (navigator.permissions?.query) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: "geolocation" as PermissionName,
          });

          if (permissionStatus.state === "denied") {
            console.warn(
              "Geolocation permission was previously denied. Please enable location services in your browser settings."
            );
            return;
          }
        } catch (_error) {
          // Continue anyway - Safari/iOS doesn't support permissions API for geolocation
          // This is expected and not an error
        }
      }

      setIsGeolocating(true);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!isMountedRef.current) return;

          const { latitude, longitude } = position.coords;

          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [longitude, latitude],
              zoom: 15,
              duration: 2000,
            });
          }

          setIsGeolocating(false);
        },
        (error) => {
          if (!isMountedRef.current) return;

          console.warn("Geolocation error:", {
            code: error.code,
            message: error.message,
            PERMISSION_DENIED: error.PERMISSION_DENIED,
            POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
            TIMEOUT: error.TIMEOUT,
          });

          // Provide specific error messages
          switch (error.code) {
            case error.PERMISSION_DENIED:
              console.warn("Location access denied. Please enable location services in your browser settings.");
              break;
            case error.POSITION_UNAVAILABLE:
              console.warn("Location information unavailable.");
              break;
            case error.TIMEOUT:
              console.warn("Location request timed out.");
              break;
            default:
              console.warn("Error getting location:", error.message);
              break;
          }

          setIsGeolocating(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0, // Don't use cached position - always get fresh location
        }
      );
    }
  };

  // If no controls are shown, render nothing
  if (!showCompass && !showGeolocate && !showResetZoom) {
    return null;
  }

  // Determine which button is visually first (for rounded corners)
  // Reset zoom is always first if shown
  // Then compass (mobile only)
  // Then geolocate
  const isCompassFirst = !showResetZoom && showCompass;
  const isGeolocateFirst = !showResetZoom && !showCompass && showGeolocate;

  // Determine which button is visually last
  const isCompassLast = !showGeolocate && showCompass;
  const isResetZoomLast = !showGeolocate && !showCompass && showResetZoom;

  return (
    <ControlGroup position={position} className={className} style={style} spacing="sm">
      {/* Reset zoom - always at top when shown */}
      {showResetZoom && (
        <ControlButton
          icon="ArrowsOut"
          onPress={onResetZoom}
          ariaLabel="Reset zoom to show all points"
          className={twMerge(
            // Top button: round top corners, flat bottom
            !isResetZoomLast && "rounded-b-none",
            // If compass is below, remove bottom border
            showCompass && "border-b-0"
          )}
        />
      )}

      {/* Compass - resets map bearing to north */}
      {showCompass && (
        <ControlButton
          icon="Compass"
          onPress={handleCompassClick}
          ariaLabel="Reset map orientation"
          className={twMerge(
            // Middle button: flat top and bottom
            !isCompassFirst && "rounded-t-none border-t-0",
            !isCompassLast && "rounded-b-none border-b-0"
          )}
        />
      )}

      {/* Geolocate - always at bottom when shown */}
      {showGeolocate && (
        <ControlButton
          icon={isGeolocating ? "Spinner" : "NavigationArrow"}
          onPress={handleGeolocateClick}
          isDisabled={isGeolocating}
          ariaLabel="Go to my location"
          iconClassName={twMerge("text-text-primary", isGeolocating && "animate-spin")}
          className={twMerge(
            // Bottom button: flat top, round bottom
            !isGeolocateFirst && "rounded-t-none border-t-0",
            // On desktop when compass is hidden, need to handle top rounding
            !isGeolocateFirst && showCompass && "lg:rounded-t lg:border-t"
          )}
        />
      )}
    </ControlGroup>
  );
}
